import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Plus } from "lucide-react";

interface Coin {
  id: number;
  symbol: string;
  name: string;
  currentPrice: number;
  marketCap: number;
  targetPrice?: number;
}

export default function CoinsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [targetPrice, setTargetPrice] = useState<Record<number, string>>({});

  // Fetch user's coins
  const { data: coins, isLoading } = useQuery<Coin[]>({
    queryKey: ["/api/coins"],
  });

  // Add coin to watchlist
  const addCoin = useMutation({
    mutationFn: async (values: { symbol: string }) => {
      const res = await fetch("/api/coins", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/coins"] });
      toast({ title: "Coin added successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error adding coin",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Set target price for a coin
  const setPrice = useMutation({
    mutationFn: async ({ coinId, targetPrice }: { coinId: number; targetPrice: number }) => {
      const res = await fetch(`/api/coins/${coinId}/target`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ targetPrice }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/coins"] });
      toast({ title: "Target price set successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error setting target price",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="min-h-screen bg-black">
      <div className="container mx-auto py-8">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white">Coin Watchlist</CardTitle>
            <CardDescription>Track your favorite cryptocurrencies</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Add new coin form */}
              <div className="flex gap-4">
                <Input
                  placeholder="Enter coin symbol (e.g., BTC)"
                  className="bg-zinc-800 border-zinc-700"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      const target = e.target as HTMLInputElement;
                      addCoin.mutate({ symbol: target.value.toUpperCase() });
                      target.value = '';
                    }
                  }}
                />
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Coin
                </Button>
              </div>

              {/* Coins table */}
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : coins?.length ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Current Price</TableHead>
                      <TableHead>Market Cap</TableHead>
                      <TableHead>Target Price</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {coins.map((coin) => (
                      <TableRow key={coin.id}>
                        <TableCell className="font-medium">{coin.symbol}</TableCell>
                        <TableCell>{coin.name}</TableCell>
                        <TableCell>${coin.currentPrice.toFixed(2)}</TableCell>
                        <TableCell>${(coin.marketCap / 1e6).toFixed(2)}M</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={targetPrice[coin.id] ?? coin.targetPrice ?? ''}
                            onChange={(e) => setTargetPrice({
                              ...targetPrice,
                              [coin.id]: e.target.value
                            })}
                            className="w-32 bg-zinc-800 border-zinc-700"
                            placeholder="Set target..."
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              const price = parseFloat(targetPrice[coin.id]);
                              if (!isNaN(price)) {
                                setPrice.mutate({ coinId: coin.id, targetPrice: price });
                                setTargetPrice((prev) => {
                                  const next = { ...prev };
                                  delete next[coin.id];
                                  return next;
                                });
                              }
                            }}
                            disabled={!targetPrice[coin.id]}
                          >
                            Set Target
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  No coins added to your watchlist yet
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
